import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { useNavigate, useParams } from 'react-router-dom'
import {
  ArrowLeft,
  Calendar,
  Clock,
  Activity,
  Settings,
  CheckCircle,
  AlertCircle,
  Plus,
  Heart,
  Video,
  MapPin,
  User,
  Edit,
  Save,
  X,
} from 'lucide-react'
import {
  fetchAllPreferences,
  fetchRecommendation,
  fetchSessions,
  upsertRecommendation,
} from '../../lib/instantData'
import { brandAlert } from '../../contexts/DialogContext'
import GlassCard from '../../components/ui/GlassCard'
import PanelShell from '../../components/ui/PanelShell'
import GradientButton from '../../components/ui/GradientButton'

interface UpcomingSession {
  id: string
  title: string
  type: 'online' | 'in-person'
  date: string
  time: string
  trainer: string
  status: string
  client_join_url?: string
  admin_joined?: boolean
}

interface RecentTrainingSession {
  id: string
  title: string
  type: 'online' | 'in-person'
  date: string
  time: string
  status: 'completed' | 'cancelled' | 'no-show'
  trainer: string
}

interface Client {
  id: string
  email: string
  full_name?: string
  created_at: string
  avatar_url?: string
  last_login?: string
  phone?: string
  location?: string
  date_of_birth?: string
  fitness_goals?: string[]
  workout_time?: string
  user_id?: string
}

const AdminUserDashboard: React.FC = () => {
  const navigate = useNavigate()
  const { clientId } = useParams<{ clientId: string }>()
  
  const [client, setClient] = useState<Client | null>(null)
  const [upcomingSessions, setUpcomingSessions] = useState<UpcomingSession[]>([])
  const [recentTrainingSessions, setRecentTrainingSessions] = useState<RecentTrainingSession[]>([])
  const [loading, setLoading] = useState(true)
  const [isNewUser, setIsNewUser] = useState(false)
  const [isEditingRecommendations, setIsEditingRecommendations] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [saveSuccess, setSaveSuccess] = useState(false)
  const [recommendations, setRecommendations] = useState({
    nonTrainingDayPlan: [
      "Complete 30 minutes of light cardio (walking, cycling, or swimming)",
      "Focus on stretching and mobility work for 15 minutes",
      "Stay hydrated and aim for 8 hours of sleep tonight",
      "Review your meal plan and prep healthy snacks for tomorrow"
    ],
    personalNote: "Great work this week! Your consistency is really showing. Remember, recovery days are just as important as training days. Listen to your body and don't push too hard on your rest days. You're making excellent progress!"
  })
  const [editingRecommendations, setEditingRecommendations] = useState({
    nonTrainingDayPlan: [
      "Complete 30 minutes of light cardio (walking, cycling, or swimming)",
      "Focus on stretching and mobility work for 15 minutes",
      "Stay hydrated and aim for 8 hours of sleep tonight",
      "Review your meal plan and prep healthy snacks for tomorrow"
    ],
    personalNote: "Great work this week! Your consistency is really showing. Remember, recovery days are just as important as training days. Listen to your body and don't push too hard on your rest days. You're making excellent progress!"
  })
  
  // Add state for tracking icon colors
  const [iconColors, setIconColors] = useState<string[]>([
    'bg-green-500',
    'bg-brand', 
    'bg-brand-dark',
    'bg-pink-500'
  ])

  useEffect(() => {
    if (clientId) {
      fetchClientData()
    }
  }, [clientId])

  const fetchClientData = async () => {
    if (!clientId) return

    try {
      const preferences = await fetchAllPreferences()
      const clientData =
        preferences.find((p) => p.id === clientId || p.user_id === clientId) || null

      if (clientData) {
        setClient(clientData as any)

        if (clientData.created_at) {
          const createdAt = new Date(clientData.created_at)
          const hoursDifference = (Date.now() - createdAt.getTime()) / (1000 * 3600)
          setIsNewUser(hoursDifference < 24)
        }

        await fetchUserSessions(clientData.user_id)
        await fetchClientRecommendations(clientData.user_id)
      }
    } catch (error) {
      console.error('Error fetching client data:', error)
    } finally {
      setLoading(false)
    }
  }

  const fetchUserSessions = async (userId: string) => {
    if (!userId) return

    try {
      const all = await fetchSessions({ userId })
      setUpcomingSessions(
        all
          .filter((s) => s.status === 'scheduled')
          .map((session) => ({
            id: session.id,
            title: `${session.session_type === 'online' ? 'Online' : 'Private'} Training Session`,
            type: session.session_type === 'online' ? 'online' : 'in-person',
            date: session.scheduled_date,
            time: new Date(session.scheduled_date).toLocaleTimeString([], {
              hour: '2-digit',
              minute: '2-digit',
            }),
            trainer: 'Cassie',
            status: session.status,
            client_join_url: session.client_join_url,
            admin_joined: session.admin_joined,
          }))
      )

      setRecentTrainingSessions(
        all
          .filter((s) => s.status === 'completed' || s.status === 'cancelled')
          .slice(0, 5)
          .map((session) => ({
            id: session.id,
            title: `${session.session_type === 'online' ? 'Online' : 'Private'} Training Session`,
            type: session.session_type === 'online' ? 'online' : 'in-person',
            date: session.scheduled_date,
            time: new Date(session.scheduled_date).toLocaleTimeString([], {
              hour: '2-digit',
              minute: '2-digit',
            }),
            status: session.status === 'completed' ? 'completed' : 'cancelled',
            trainer: 'Cassie',
          }))
      )
    } catch (error) {
      console.error('Error fetching user sessions:', error)
    }
  }

  const fetchClientRecommendations = async (userId: string) => {
    if (!userId) return

    try {
      const recommendationsData = await fetchRecommendation(userId)
      if (recommendationsData) {
        setRecommendations({
          nonTrainingDayPlan: recommendationsData.non_training_day_plan?.length
            ? recommendationsData.non_training_day_plan
            : [
                'Complete 30 minutes of light cardio (walking, cycling, or swimming)',
                'Focus on stretching and mobility work for 15 minutes',
                'Stay hydrated and aim for 8 hours of sleep tonight',
                'Review your meal plan and prep healthy snacks for tomorrow',
              ],
          personalNote:
            recommendationsData.personal_note ||
            "Great work this week! Your consistency is really showing. Remember, recovery days are just as important as training days.",
        })
        if (recommendationsData.icon_colors?.length) {
          setIconColors(recommendationsData.icon_colors)
        }
      }
    } catch (error) {
      console.error('Error fetching client recommendations:', error)
    }
  }

  const getSessionStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-4 h-4 text-green-500" />
      case 'cancelled':
        return <AlertCircle className="w-4 h-4 text-red-500" />
      case 'no-show':
        return <AlertCircle className="w-4 h-4 text-orange-500" />
      default:
        return <Clock className="w-4 h-4 text-gray-500" />
    }
  }

  const renderEmptyState = (title: string, description: string, actionText?: string, onAction?: () => void) => (
    <div className="text-center py-8">
      <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center">
        <Calendar className="w-8 h-8 text-gray-400" />
      </div>
      <h3 className="text-lg font-semibold text-ink mb-2">{title}</h3>
      <p className="text-gray-600 text-sm mb-4">{description}</p>
      {actionText && onAction && (
        <GradientButton onClick={onAction}>
          {actionText}
        </GradientButton>
      )}
    </div>
  )

  const handleStartEditing = () => {
    setEditingRecommendations({
      nonTrainingDayPlan: [...recommendations.nonTrainingDayPlan],
      personalNote: recommendations.personalNote
    })
    // Ensure icon colors array matches the number of recommendations
    const currentColors = [...iconColors]
    while (currentColors.length < recommendations.nonTrainingDayPlan.length) {
      currentColors.push('bg-green-500')
    }
    setIconColors(currentColors)
    setIsEditingRecommendations(true)
  }

  const handleCancelEditing = () => {
    setIsEditingRecommendations(false)
  }

  const handleSaveRecommendations = async () => {
    try {
      setIsSaving(true)
      setSaveSuccess(false)

      if (!client?.user_id) {
        void brandAlert({ title: 'Something went wrong', message: 'Missing client user id', variant: 'error' })
        return
      }

      await upsertRecommendation(client.user_id, {
        nonTrainingDayPlan: editingRecommendations.nonTrainingDayPlan,
        personalNote: editingRecommendations.personalNote,
        iconColors,
      })

      setRecommendations(editingRecommendations)
      setIsEditingRecommendations(false)
      setSaveSuccess(true)
      setTimeout(() => setSaveSuccess(false), 2000)
      setTimeout(() => {
        if (client.user_id) fetchClientRecommendations(client.user_id)
      }, 100)
    } catch (error) {
      console.error('Error saving recommendations:', error)
      void brandAlert({ title: 'Something went wrong', message: 'Error saving recommendations. Please try again.', variant: 'error' })
    } finally {
      setIsSaving(false)
    }
  }

  const updateNonTrainingDayPlan = (index: number, value: string) => {
    const updated = [...editingRecommendations.nonTrainingDayPlan]
    updated[index] = value
    setEditingRecommendations({
      ...editingRecommendations,
      nonTrainingDayPlan: updated
    })
  }

  const addNonTrainingDayPlanItem = () => {
    setEditingRecommendations({
      ...editingRecommendations,
      nonTrainingDayPlan: [...editingRecommendations.nonTrainingDayPlan, "New recommendation"]
    })
    // Add a default color for the new item
    setIconColors([...iconColors, 'bg-green-500'])
  }

  const removeNonTrainingDayPlanItem = (index: number) => {
    const updated = editingRecommendations.nonTrainingDayPlan.filter((_, i) => i !== index)
    setEditingRecommendations({
      ...editingRecommendations,
      nonTrainingDayPlan: updated
    })
    // Remove the corresponding color
    const updatedColors = iconColors.filter((_, i) => i !== index)
    setIconColors(updatedColors)
  }

  const cycleIconColor = (index: number) => {
    const colors = ['bg-green-500', 'bg-brand', 'bg-brand-dark', 'bg-pink-500', 'bg-yellow-500', 'bg-red-500', 'bg-ink-soft', 'bg-teal-500']
    const currentColor = iconColors[index] || 'bg-green-500'
    const currentIndex = colors.indexOf(currentColor)
    const nextIndex = (currentIndex + 1) % colors.length
    
    const newColors = [...iconColors]
    newColors[index] = colors[nextIndex]
    setIconColors(newColors)
  }

  if (loading) {
    return (
      <div className="panel-page flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-gold"></div>
      </div>
    )
  }

  if (!client) {
    return (
      <div className="panel-page">
        <div className="panel-container">
          <div className="text-center py-12">
            <User className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500 text-lg">Client not found.</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <PanelShell
      eyebrow="Client dashboard"
      title={client.full_name || 'Client'}
      subtitle="Edit recommendations and review this client's upcoming sessions."
      actions={
        <>
          <GradientButton
            onClick={() => navigate('/admin/user-dashboards')}
            variant="outline"
            size="sm"
            icon={ArrowLeft}
          >
            All clients
          </GradientButton>
          <GradientButton
            onClick={() => navigate('/admin/dashboard')}
            size="sm"
            icon={Settings}
          >
            Admin home
          </GradientButton>
        </>
      }
    >

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Upcoming Sessions */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3 }}
            className="lg:col-span-2"
          >
            <GlassCard className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-display text-xl font-semibold text-ink flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-brand" />
                  Upcoming Sessions
                </h2>
                <div className="flex items-center gap-2">
                  <button 
                    onClick={() => fetchUserSessions(client.user_id!)}
                    className="text-brand hover:text-brand-dark transition-colors text-sm font-medium"
                    title="Refresh sessions"
                  >
                    Refresh
                  </button>
                  <button 
                    onClick={() => navigate('/admin/sessions')}
                    className="text-brand hover:text-brand-dark transition-colors text-sm font-medium"
                  >
                    View All
                  </button>
                </div>
              </div>
              
              {upcomingSessions.length === 0 ? (
                renderEmptyState(
                  "No Upcoming Sessions",
                  isNewUser 
                    ? "Schedule your first training session to get started!" 
                    : "You don't have any sessions scheduled yet.",
                  "Schedule Session",
                  () => navigate('/admin/sessions')
                )
              ) : (
                <div className="space-y-4">
                  {upcomingSessions.map((session) => (
                    <motion.div
                      key={session.id}
                      whileHover={{ scale: 1.01 }}
                      className="p-4 rounded-lg bg-cream border border-gray-100 hover:border-primary/30 transition-all duration-300"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            {session.type === 'online' ? (
                              <Video className="w-4 h-4 text-blue-500" />
                            ) : (
                              <MapPin className="w-4 h-4 text-green-500" />
                            )}
                            <h3 className="font-semibold text-ink">{session.title}</h3>
                          </div>
                          <div className="flex items-center gap-4 text-sm text-gray-600">
                            <span className="flex items-center gap-1">
                              <Calendar className="w-4 h-4" />
                              {new Date(session.date).toLocaleDateString()}
                            </span>
                            <span className="flex items-center gap-1">
                              <Clock className="w-4 h-4" />
                              {session.time}
                            </span>
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              session.type === 'online' 
                                ? 'panel-chip-brand' 
                                : 'panel-chip-gold'
                            }`}>
                              {session.type === 'online' ? 'Online Training' : 'Private Training'}
                            </span>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
            </GlassCard>
          </motion.div>

          {/* Recent Activity */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.4 }}
          >
            <GlassCard className="p-6">
              <h2 className="font-display text-xl font-semibold text-ink mb-6 flex items-center gap-2">
                <Activity className="w-5 h-5 text-brand" />
                Recent Training Sessions
              </h2>
              
              {recentTrainingSessions.length === 0 ? (
                <div className="text-center py-8">
                  <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                    <Activity className="w-8 h-8 text-gray-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-ink mb-2">No Recent Sessions</h3>
                  <p className="text-gray-600 text-sm">
                    {isNewUser 
                      ? "Your completed sessions will appear here after your first workout." 
                      : "Complete a session to see your training history here."}
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {recentTrainingSessions.map((session) => (
                    <div key={session.id} className="flex items-start gap-3">
                      <div className="flex-shrink-0 mt-1">
                        {session.type === 'online' ? (
                          <Video className="w-4 h-4 text-blue-500" />
                        ) : (
                          <MapPin className="w-4 h-4 text-green-500" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-ink truncate">
                          {session.title}
                        </p>
                        <div className="flex items-center gap-2 text-xs text-gray-600">
                          <span>{new Date(session.date).toLocaleDateString()}</span>
                          <span>•</span>
                          <span>{session.time}</span>
                          <span>•</span>
                          <span>{session.trainer}</span>
                        </div>
                      </div>
                      <div className="flex-shrink-0">
                        {getSessionStatusIcon(session.status)}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </GlassCard>
          </motion.div>
        </div>

        {/* Cassie's Recommendations Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="mt-8"
        >
          <GlassCard className="p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-display text-xl font-semibold text-ink flex items-center gap-2">
                <User className="w-5 h-5 text-brand" />
                Cassie's Recommendations
              </h2>
              <div className="flex items-center gap-2">
                <div className="text-xs text-gray-600 bg-cream px-2 py-1 rounded">
                  Updated: {new Date().toLocaleDateString()}
                </div>
                <button
                  onClick={isEditingRecommendations ? handleSaveRecommendations : handleStartEditing}
                  disabled={isSaving}
                  className={`flex items-center gap-2 px-3 py-1 rounded text-sm font-medium transition-colors ${
                    isEditingRecommendations 
                      ? saveSuccess 
                        ? 'bg-green-600 text-white' 
                        : 'bg-brand hover:bg-brand-dark text-white'
                      : 'btn-gold text-sm'
                  } ${isSaving ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  {isEditingRecommendations ? <Save className="w-4 h-4" /> : <Edit className="w-4 h-4" />}
                  {isEditingRecommendations 
                    ? isSaving 
                      ? 'Saving...' 
                      : saveSuccess 
                        ? 'Saved!' 
                        : 'Save'
                    : 'Edit'
                  }
                </button>
                {isEditingRecommendations && (
                  <button
                    onClick={handleCancelEditing}
                    className="flex items-center gap-2 px-3 py-1 rounded text-sm font-medium bg-gray-500 hover:bg-gray-600 text-white transition-colors"
                  >
                    <X className="w-4 h-4" />
                    Cancel
                  </button>
                )}
              </div>
            </div>
            
            <div className="space-y-4">
              <div className="p-4 rounded-lg bg-gradient-to-r from-gold-soft to-cream border border-gold/20">
                <h3 className="text-ink font-semibold mb-2">Your Non-Training Day Plan</h3>
                <div className="space-y-3 text-sm">
                  {isEditingRecommendations ? (
                    <>
                      {editingRecommendations.nonTrainingDayPlan.map((item, index) => (
                        <div key={index} className="flex items-start gap-2">
                          <button
                            onClick={() => cycleIconColor(index)}
                            className={`w-2 h-2 ${iconColors[index] || 'bg-green-500'} rounded-full mt-2 flex-shrink-0 hover:scale-110 transition-all duration-200 cursor-pointer`}
                            title="Click to change color"
                          ></button>
                          <div className="flex-1 flex items-center gap-2">
                            <input
                              type="text"
                              value={item}
                              onChange={(e) => updateNonTrainingDayPlan(index, e.target.value)}
                              className="flex-1 bg-cream border border-white/20 rounded px-2 py-1 text-gray-600 text-sm focus:outline-none focus:border-brand"
                            />
                            <button
                              onClick={() => removeNonTrainingDayPlanItem(index)}
                              className="text-red-400 hover:text-red-300 transition-colors"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      ))}
                      <button
                        onClick={addNonTrainingDayPlanItem}
                        className="flex items-center gap-2 text-brand hover:text-brand-dark text-sm font-medium"
                      >
                        <Plus className="w-4 h-4" />
                        Add recommendation
                      </button>
                    </>
                  ) : (
                    recommendations.nonTrainingDayPlan.map((item, index) => (
                      <div key={index} className="flex items-start gap-2">
                        <div className={`w-2 h-2 ${iconColors[index] || 'bg-green-500'} rounded-full mt-2 flex-shrink-0`}></div>
                        <p className="text-gray-600">{item}</p>
                      </div>
                    ))
                  )}
                </div>
              </div>

              <div className="p-4 rounded-lg bg-cream border border-gray-100">
                <div className="flex items-center gap-2 mb-3">
                  <Heart className="w-4 h-4 text-pink-500" />
                  <span className="text-ink font-medium">Personal Note from Cassie</span>
                </div>
                {isEditingRecommendations ? (
                  <textarea
                    value={editingRecommendations.personalNote}
                    onChange={(e) => setEditingRecommendations({
                      ...editingRecommendations,
                      personalNote: e.target.value
                    })}
                    className="w-full form-input text-ink text-sm italic focus:outline-none focus:border-brand resize-none"
                    rows={3}
                  />
                ) : (
                  <p className="text-gray-600 text-sm italic">
                    "{recommendations.personalNote}"
                  </p>
                )}
              </div>
            </div>
          </GlassCard>
        </motion.div>
    </PanelShell>
  )
}

export default AdminUserDashboard 